import React, { useEffect, useState, useCallback } from 'react';
import { FiSave } from 'react-icons/fi';
import { getRates, saveScalar, saveBracket } from './payrollApi';

const pct = (n: any) => `${(Number(n) * 100).toFixed(3)}%`;

export default function RatesTab() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [scalars, setScalars] = useState<any[]>([]);
  const [brackets, setBrackets] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingId, setSavingId] = useState<string>('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { const d = await getRates(year); setScalars(d.scalars); setBrackets(d.brackets); }
    catch (e: any) { setError(e.message || 'Could not load rates'); }
    finally { setLoading(false); }
  }, [year]);
  useEffect(() => { load(); }, [load]);

  const editScalar = (id: number, v: string) =>
    setScalars((rows) => rows.map((r) => r.pay_jurisdiction_rate_id === id ? { ...r, rate_value: v, _dirty: true } : r));
  const editBracket = (id: number, field: string, v: string) =>
    setBrackets((rows) => rows.map((r) => r.pay_tax_bracket_id === id ? { ...r, [field]: v, _dirty: true } : r));

  const persistScalar = async (r: any) => {
    setSavingId('s' + r.pay_jurisdiction_rate_id); setError('');
    try { await saveScalar({ tax_year: r.tax_year, jurisdiction: r.jurisdiction, rate_key: r.rate_key, rate_value: Number(r.rate_value), description: r.description }); await load(); }
    catch (e: any) { setError(e.message || 'Could not save'); }
    finally { setSavingId(''); }
  };
  const persistBracket = async (r: any) => {
    setSavingId('b' + r.pay_tax_bracket_id); setError('');
    try { await saveBracket({ tax_year: r.tax_year, jurisdiction: r.jurisdiction, bracket_order: r.bracket_order, income_from: Number(r.income_from), income_to: r.income_to === '' || r.income_to == null ? null : Number(r.income_to), rate: Number(r.rate) }); await load(); }
    catch (e: any) { setError(e.message || 'Could not save'); }
    finally { setSavingId(''); }
  };

  const jurisdictions = Array.from(new Set(brackets.map((b) => b.jurisdiction)));

  return (
    <div>
      <div className="pay-toolbar">
        <div className="pay-field" style={{ maxWidth: 140 }}>
          <label>Tax year</label>
          <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {[year + 1, year, year - 1, year - 2].filter((v, i, a) => a.indexOf(v) === i).map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>
      <div className="pay-note" style={{ marginBottom: 'var(--spacing-md)' }}>
        ⚠️ Verify these against CRA's official <strong>T4127 Payroll Deductions Formulas</strong> each tax year. The engine is exact; these values are data you maintain. Quebec (QPP/QPIP) is approximated — review QC runs manually.
      </div>
      {error && <div className="pay-error">{error}</div>}
      {loading ? <div className="pay-empty">Loading…</div> : (
        <>
          <h3 style={{ marginTop: 0 }}>Statutory constants (CPP / EI / basic amounts)</h3>
          <div className="pay-card" style={{ padding: 0, overflowX: 'auto', marginBottom: 'var(--spacing-xl)' }}>
            <table className="pay-table">
              <thead><tr><th>Jurisdiction</th><th>Key</th><th className="num">Value</th><th>Description</th><th></th></tr></thead>
              <tbody>
                {scalars.map((r) => (
                  <tr key={r.pay_jurisdiction_rate_id}>
                    <td>{r.jurisdiction}</td><td>{r.rate_key}</td>
                    <td className="num"><input style={{ width: 120, textAlign: 'right' }} value={r.rate_value} onChange={(e) => editScalar(r.pay_jurisdiction_rate_id, e.target.value)} /></td>
                    <td style={{ whiteSpace: 'normal', color: 'var(--secondary)', fontSize: 'var(--font-size-xs)' }}>{r.description}</td>
                    <td className="num">{r._dirty && <button className="pay-btn sm" onClick={() => persistScalar(r)} disabled={savingId === 's' + r.pay_jurisdiction_rate_id}><FiSave /></button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3>Income tax brackets</h3>
          {jurisdictions.map((j) => (
            <div key={j} style={{ marginBottom: 'var(--spacing-lg)' }}>
              <strong>{j === 'CA' ? 'Federal (CA)' : j}</strong>
              <div className="pay-card" style={{ padding: 0, overflowX: 'auto', marginTop: 6 }}>
                <table className="pay-table">
                  <thead><tr><th className="num">#</th><th className="num">From</th><th className="num">To</th><th className="num">Rate</th><th></th></tr></thead>
                  <tbody>
                    {brackets.filter((b) => b.jurisdiction === j).map((b) => (
                      <tr key={b.pay_tax_bracket_id}>
                        <td className="num">{b.bracket_order}</td>
                        <td className="num"><input style={{ width: 110, textAlign: 'right' }} value={b.income_from} onChange={(e) => editBracket(b.pay_tax_bracket_id, 'income_from', e.target.value)} /></td>
                        <td className="num"><input style={{ width: 110, textAlign: 'right' }} value={b.income_to ?? ''} placeholder="∞" onChange={(e) => editBracket(b.pay_tax_bracket_id, 'income_to', e.target.value)} /></td>
                        <td className="num"><input style={{ width: 80, textAlign: 'right' }} value={b.rate} onChange={(e) => editBracket(b.pay_tax_bracket_id, 'rate', e.target.value)} /> <span style={{ color: 'var(--secondary)', fontSize: 'var(--font-size-xs)' }}>{pct(b.rate)}</span></td>
                        <td className="num">{b._dirty && <button className="pay-btn sm" onClick={() => persistBracket(b)} disabled={savingId === 'b' + b.pay_tax_bracket_id}><FiSave /></button>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
